/**
 * Main transformation pipeline: Forecast → Breakdown Cells
 * Simple, linear flow: enrich → filter → extract → aggregate → enhance
 */

import { BreakdownCell } from '@/components/ui/breakdown-tables/types';
import { TransformInput, EnrichedBreakdown } from './types';
import { filterByTimePreset, getReferenceDate } from './timeFiltering';
import { extractCoordinates, getDimensionValues } from './dimensionExtraction';
import { enhanceWithYOY } from './yoyEnhancement';
import { expandYOYToColumns } from './yoyColumnExpansion';
import { DimensionType, TimeFilterPreset } from '@/types/rebateAnalytics';
import { ALL_MONTHS } from '@/utils/rebateForecastingUtils';

/**
 * STEP 1: Flatten and enrich all breakdowns from points
 * Attaches metadata (tags, conditional flag) to each breakdown
 */
function flattenAndEnrich(input: TransformInput): EnrichedBreakdown[] {
  const enrichedBreakdowns: EnrichedBreakdown[] = [];

  // Build lookup maps for fast metadata access
  const ruleMetadataMap = new Map(input.rules.map((r) => [r.id, r]));
  const agreementMetadataMap = new Map(input.agreements.map((a) => [a.id, a]));
  const supplierMetadataMap = new Map(input.suppliers.map((s) => [s.id, s]));

  // Process current year points
  for (const point of input.currentYearPoints) {
    // Ensure date is a valid Date object
    let pointDate: Date;
    if (point.date instanceof Date) {
      pointDate = point.date;
    } else if (typeof point.date === 'string') {
      pointDate = new Date(point.date);
    } else {
      console.warn('Skipping point with invalid date:', point);
      continue;
    }

    // Validate date value
    if (isNaN(pointDate.getTime())) {
      console.warn('Skipping point with invalid date value:', point);
      continue;
    }

    for (const breakdown of point.breakdowns) {
      const ruleMetadata = ruleMetadataMap.get(breakdown.ruleId);
      const agreementMetadata = agreementMetadataMap.get(breakdown.agreementId);
      const supplierMetadata = supplierMetadataMap.get(breakdown.supplierId);

      enrichedBreakdowns.push({
        ...breakdown,
        date: pointDate,
        isConditional: ruleMetadata?.isConditional ?? false,
        ruleTags: ruleMetadata?.tags || [],
        agreementTags: agreementMetadata?.tags || [],
        supplierTags: supplierMetadata?.tags || [],
      });
    }
  }

  return enrichedBreakdowns;
}

/**
 * Collect all unique values for each dimension from enriched breakdowns
 * Also collects tag dimensions from rules/agreements metadata to ensure completeness
 */
function collectAllDimensionValues(
  enrichedBreakdowns: EnrichedBreakdown[],
  input: TransformInput,
): {
  primaryXValues: Set<string>;
  secondaryXValues: Set<string | null>;
  primaryYValues: Set<string>;
  secondaryYValues: Set<string | null>;
} {
  const primaryXValues = new Set<string>();
  const secondaryXValues = new Set<string | null>();
  const primaryYValues = new Set<string>();
  const secondaryYValues = new Set<string | null>();

  // Collect from breakdowns
  for (const breakdown of enrichedBreakdowns) {
    // Collect primary X values
    const pxValues = getDimensionValues(
      breakdown,
      input.dimensions.primaryX,
      input.groupingConfigs?.get(input.dimensions.primaryX),
    );
    for (const val of pxValues) {
      primaryXValues.add(val);
    }

    // Collect secondary X values
    if (input.dimensions.secondaryX) {
      const sxValues = getDimensionValues(
        breakdown,
        input.dimensions.secondaryX,
        input.groupingConfigs?.get(input.dimensions.secondaryX),
      );
      for (const val of sxValues) {
        secondaryXValues.add(val);
      }
    } else {
      secondaryXValues.add(null);
    }

    // Collect primary Y values
    const pyValues = getDimensionValues(
      breakdown,
      input.dimensions.primaryY,
      input.groupingConfigs?.get(input.dimensions.primaryY),
    );
    for (const val of pyValues) {
      primaryYValues.add(val);
    }

    // Collect secondary Y values
    if (input.dimensions.secondaryY) {
      const syValues = getDimensionValues(
        breakdown,
        input.dimensions.secondaryY,
        input.groupingConfigs?.get(input.dimensions.secondaryY),
      );
      for (const val of syValues) {
        secondaryYValues.add(val);
      }
    } else {
      secondaryYValues.add(null);
    }
  }

  // When primaryXDimension is TIME, conditionally add months based on timeFilterPreset
  // This ensures months with zero values still appear as columns, but respects the filter preset
  if (input.dimensions.primaryX === DimensionType.TIME) {
    // Use reference date that accounts for past years (Dec 31 for past years)
    const referenceDate = getReferenceDate(input.currentYear);
    const referenceMonthIndex = referenceDate.getUTCMonth();

    if (input.timeFilterPreset === 'ytd') {
      // Add Jan through reference month only
      // For past years, this will be Jan-Dec (month index 11)
      // For current year, this will be Jan through actual current month
      const ytdMonths = ALL_MONTHS.slice(0, referenceMonthIndex + 1);
      for (const month of ytdMonths) {
        primaryXValues.add(month);
      }
    } else if (
      input.timeFilterPreset === 'all' ||
      input.timeFilterPreset === 'yoy'
    ) {
      // Add all 12 months
      for (const month of ALL_MONTHS) {
        primaryXValues.add(month);
      }
    } else if (input.timeFilterPreset === 'current') {
      // Add only reference month (will be expanded to 3 columns via YOY expansion)
      // For past years, this will be December
      // For current year, this will be the actual current month
      primaryXValues.add(ALL_MONTHS[referenceMonthIndex]);
    } else {
      // For other presets (previous, prev_and_current, current_and_next), add all months
      // This maintains existing behavior for these presets
      for (const month of ALL_MONTHS) {
        primaryXValues.add(month);
      }
    }
  }

  // Collect catch-all groups from grouping configs to ensure they're always included
  // Also collect from rules/agreements metadata when no grouping config exists
  const primaryXGrouping = input.groupingConfigs?.get(
    input.dimensions.primaryX,
  );
  if (primaryXGrouping) {
    // Add catch-all groups
    for (const catchAllGroup of primaryXGrouping.catchAllGroups) {
      primaryXValues.add(catchAllGroup.name);
    }
    // Add user group names
    for (const userGroup of primaryXGrouping.userGroups) {
      primaryXValues.add(userGroup.name);
    }
  } else {
    // No grouping: collect from metadata (for tag dimensions)
    if (input.dimensions.primaryX === DimensionType.RULE_TAG) {
      for (const rule of input.rules) {
        if (rule.tags && rule.tags.length > 0) {
          for (const tag of rule.tags) {
            primaryXValues.add(tag.name);
          }
        } else {
          const isConditional = rule.isConditional ?? false;
          primaryXValues.add(
            isConditional ? 'Conditional (Growth)' : 'Unconditional (Volume)',
          );
        }
      }
    } else if (input.dimensions.primaryX === DimensionType.AGREEMENT_TAG) {
      for (const agreement of input.agreements) {
        if (agreement.tags && agreement.tags.length > 0) {
          for (const tag of agreement.tags) {
            primaryXValues.add(tag.name);
          }
        } else {
          primaryXValues.add('Standard');
        }
      }
    } else if (input.dimensions.primaryX === DimensionType.SUPPLIER_TAG) {
      for (const supplier of input.suppliers) {
        if (supplier.tags && supplier.tags.length > 0) {
          for (const tag of supplier.tags) {
            primaryXValues.add(tag.name);
          }
        } else {
          primaryXValues.add('Standard');
        }
      }
    } else if (input.dimensions.primaryX === DimensionType.RULE_TYPE) {
      // RULE_TYPE always has exactly 2 values: Conditional and Unconditional
      primaryXValues.add('Conditional');
      primaryXValues.add('Unconditional');
    }
  }

  if (input.dimensions.secondaryX) {
    const secondaryXGrouping = input.groupingConfigs?.get(
      input.dimensions.secondaryX,
    );
    if (secondaryXGrouping) {
      // Add catch-all groups
      for (const catchAllGroup of secondaryXGrouping.catchAllGroups) {
        secondaryXValues.add(catchAllGroup.name);
      }
      // Add user group names
      for (const userGroup of secondaryXGrouping.userGroups) {
        secondaryXValues.add(userGroup.name);
      }
    } else {
      // No grouping: collect from metadata (for tag dimensions)
      if (input.dimensions.secondaryX === DimensionType.RULE_TAG) {
        for (const rule of input.rules) {
          if (rule.tags && rule.tags.length > 0) {
            for (const tag of rule.tags) {
              secondaryXValues.add(tag.name);
            }
          } else {
            const isConditional = rule.isConditional ?? false;
            secondaryXValues.add(
              isConditional ? 'Conditional (Growth)' : 'Unconditional (Volume)',
            );
          }
        }
      } else if (input.dimensions.secondaryX === DimensionType.AGREEMENT_TAG) {
        for (const agreement of input.agreements) {
          if (agreement.tags && agreement.tags.length > 0) {
            for (const tag of agreement.tags) {
              secondaryXValues.add(tag.name);
            }
          } else {
            secondaryXValues.add('Standard');
          }
        }
      } else if (input.dimensions.secondaryX === DimensionType.SUPPLIER_TAG) {
        for (const supplier of input.suppliers) {
          if (supplier.tags && supplier.tags.length > 0) {
            for (const tag of supplier.tags) {
              secondaryXValues.add(tag.name);
            }
          } else {
            secondaryXValues.add('Standard');
          }
        }
      } else if (input.dimensions.secondaryX === DimensionType.RULE_TYPE) {
        // RULE_TYPE always has exactly 2 values: Conditional and Unconditional
        secondaryXValues.add('Conditional');
        secondaryXValues.add('Unconditional');
      }
    }
  }

  const primaryYGrouping = input.groupingConfigs?.get(
    input.dimensions.primaryY,
  );
  if (primaryYGrouping) {
    // Add catch-all groups
    for (const catchAllGroup of primaryYGrouping.catchAllGroups) {
      primaryYValues.add(catchAllGroup.name);
    }
    // Add user group names
    for (const userGroup of primaryYGrouping.userGroups) {
      primaryYValues.add(userGroup.name);
    }
  } else {
    // No grouping: collect from metadata (for tag dimensions)
    if (input.dimensions.primaryY === DimensionType.RULE_TAG) {
      for (const rule of input.rules) {
        if (rule.tags && rule.tags.length > 0) {
          for (const tag of rule.tags) {
            primaryYValues.add(tag.name);
          }
        } else {
          const isConditional = rule.isConditional ?? false;
          primaryYValues.add(
            isConditional ? 'Conditional (Growth)' : 'Unconditional (Volume)',
          );
        }
      }
    } else if (input.dimensions.primaryY === DimensionType.AGREEMENT_TAG) {
      for (const agreement of input.agreements) {
        if (agreement.tags && agreement.tags.length > 0) {
          for (const tag of agreement.tags) {
            primaryYValues.add(tag.name);
          }
        } else {
          primaryYValues.add('Standard');
        }
      }
    } else if (input.dimensions.primaryY === DimensionType.SUPPLIER_TAG) {
      for (const supplier of input.suppliers) {
        if (supplier.tags && supplier.tags.length > 0) {
          for (const tag of supplier.tags) {
            primaryYValues.add(tag.name);
          }
        } else {
          primaryYValues.add('Standard');
        }
      }
    } else if (input.dimensions.primaryY === DimensionType.RULE_TYPE) {
      // RULE_TYPE always has exactly 2 values: Conditional and Unconditional
      primaryYValues.add('Conditional');
      primaryYValues.add('Unconditional');
    }
  }

  if (input.dimensions.secondaryY) {
    const secondaryYGrouping = input.groupingConfigs?.get(
      input.dimensions.secondaryY,
    );
    if (secondaryYGrouping) {
      // Add catch-all groups
      for (const catchAllGroup of secondaryYGrouping.catchAllGroups) {
        secondaryYValues.add(catchAllGroup.name);
      }
      // Add user group names
      for (const userGroup of secondaryYGrouping.userGroups) {
        secondaryYValues.add(userGroup.name);
      }
    } else {
      // No grouping: collect from metadata (for tag dimensions)
      if (input.dimensions.secondaryY === DimensionType.RULE_TAG) {
        for (const rule of input.rules) {
          if (rule.tags && rule.tags.length > 0) {
            for (const tag of rule.tags) {
              secondaryYValues.add(tag.name);
            }
          } else {
            const isConditional = rule.isConditional ?? false;
            secondaryYValues.add(
              isConditional ? 'Conditional (Growth)' : 'Unconditional (Volume)',
            );
          }
        }
      } else if (input.dimensions.secondaryY === DimensionType.AGREEMENT_TAG) {
        for (const agreement of input.agreements) {
          if (agreement.tags && agreement.tags.length > 0) {
            for (const tag of agreement.tags) {
              secondaryYValues.add(tag.name);
            }
          } else {
            secondaryYValues.add('Standard');
          }
        }
      } else if (input.dimensions.secondaryY === DimensionType.SUPPLIER_TAG) {
        for (const supplier of input.suppliers) {
          if (supplier.tags && supplier.tags.length > 0) {
            for (const tag of supplier.tags) {
              secondaryYValues.add(tag.name);
            }
          } else {
            secondaryYValues.add('Standard');
          }
        }
      } else if (input.dimensions.secondaryY === DimensionType.RULE_TYPE) {
        // RULE_TYPE always has exactly 2 values: Conditional and Unconditional
        secondaryYValues.add('Conditional');
        secondaryYValues.add('Unconditional');
      }
    }
  }

  return {
    primaryXValues,
    secondaryXValues,
    primaryYValues,
    secondaryYValues,
  };
}

/**
 * Fill in missing combinations with zero values
 * Ensures all combinations of sub-dimensions are present
 */
function fillMissingCombinations(
  cells: BreakdownCell[],
  allDimensionValues: {
    primaryXValues: Set<string>;
    secondaryXValues: Set<string | null>;
    primaryYValues: Set<string>;
    secondaryYValues: Set<string | null>;
  },
): BreakdownCell[] {
  // Create a map of existing cells for quick lookup
  const existingCellsMap = new Map<string, BreakdownCell>();
  for (const cell of cells) {
    const key = `${cell.column}|${cell.subColumn ?? 'null'}|${cell.row}|${cell.subRow ?? 'null'}`;
    existingCellsMap.set(key, cell);
  }

  // Generate all combinations and fill missing ones with zero
  const filledCells: BreakdownCell[] = [];

  for (const column of allDimensionValues.primaryXValues) {
    for (const subColumn of allDimensionValues.secondaryXValues) {
      for (const row of allDimensionValues.primaryYValues) {
        for (const subRow of allDimensionValues.secondaryYValues) {
          const key = `${column}|${subColumn ?? 'null'}|${row}|${subRow ?? 'null'}`;
          const existingCell = existingCellsMap.get(key);

          if (existingCell) {
            filledCells.push(existingCell);
          } else {
            // Create zero cell for missing combination
            filledCells.push({
              column,
              subColumn: subColumn === null ? null : subColumn,
              row,
              subRow: subRow === null ? null : subRow,
              value: 0,
            });
          }
        }
      }
    }
  }

  return filledCells;
}

/**
 * STEP 3 & 4: Extract coordinates and aggregate
 * For each breakdown, extract all coordinate combinations (tags can produce multiple)
 * Then aggregate by unique coordinates
 */
function extractAndAggregate(
  enrichedBreakdowns: EnrichedBreakdown[],
  input: TransformInput,
  allEnrichedBreakdownsForDimensionCollection?: EnrichedBreakdown[],
): BreakdownCell[] {
  // Map to store aggregated values: "column|subColumn|row|subRow" -> sum
  const cellMap = new Map<string, number>();

  for (const breakdown of enrichedBreakdowns) {
    // Extract all coordinate combinations for this breakdown
    const coordinateSets = extractCoordinates(
      breakdown,
      input.dimensions,
      input.groupingConfigs,
    );

    // Aggregate each coordinate set
    // When isCumulative is true, use cumulativeRebateAmount; otherwise use rebateAmount (incremental)
    const rebateValue = input.isCumulative
      ? (breakdown.cumulativeRebateAmount ?? breakdown.rebateAmount)
      : breakdown.rebateAmount;
    for (const coords of coordinateSets) {
      const key = `${coords.column}|${coords.subColumn ?? 'null'}|${coords.row}|${coords.subRow ?? 'null'}`;
      const currentValue = cellMap.get(key) || 0;
      cellMap.set(key, currentValue + rebateValue);
    }
  }

  // Convert map to BreakdownCell array
  const cells: BreakdownCell[] = [];

  for (const [key, value] of cellMap.entries()) {
    const [column, subColumnStr, row, subRowStr] = key.split('|');
    const subColumn = subColumnStr === 'null' ? null : subColumnStr;
    const subRow = subRowStr === 'null' ? null : subRowStr;

    cells.push({
      column,
      subColumn,
      row,
      subRow,
      value,
    });
  }

  // Fill in missing combinations with zero values if enabled
  if (input.fillMissingSubdimensions) {
    // Collect all unique dimension values from all breakdowns (current + previous year if available)
    const breakdownsForCollection =
      allEnrichedBreakdownsForDimensionCollection ?? enrichedBreakdowns;
    const allDimensionValues = collectAllDimensionValues(
      breakdownsForCollection,
      input,
    );

    // Fill in missing combinations with zero values
    return fillMissingCombinations(cells, allDimensionValues);
  }

  return cells;
}

/**
 * Transform forecast data into breakdown cells
 *
 * Pipeline:
 * 1. Flatten & enrich breakdowns from points
 * 2. Filter by time preset
 * 3. Extract 4D coordinates for each breakdown
 * 4. Aggregate by coordinates
 * 5. Add YOY data if previous year exists
 *
 * @param input - Transform configuration with forecast data and dimensions
 * @returns Flat array of breakdown cells ready for BreakdownTable
 */
export function transformForecastToBreakdownCells(
  input: TransformInput,
): BreakdownCell[] {
  // STEP 1: Flatten & enrich
  const enrichedBreakdowns = flattenAndEnrich(input);

  // STEP 2: Filter by time preset
  // Pass dataYear so filtering uses correct reference date for past years
  const filteredBreakdowns = input.timeFilterPreset
    ? filterByTimePreset(
        enrichedBreakdowns,
        input.timeFilterPreset,
        new Date(),
        false,
        input.currentYear,
      )
    : enrichedBreakdowns;

  const hasPreviousYearData =
    input.previousYearPoints && input.previousYearPoints.length > 0;

  let previousFilteredBreakdowns: EnrichedBreakdown[] = [];
  if (hasPreviousYearData && input.previousYearPoints) {
    const previousEnrichedBreakdowns = flattenAndEnrich({
      ...input,
      currentYearPoints: input.previousYearPoints,
      previousYearPoints: null, // Don't recurse
    });
    // For YOY-style presets, ignore year when filtering previous data
    const presetsIgnoringYear: TimeFilterPreset[] = [
      'current',
      'previous',
      'prev_and_current',
      'current_and_next',
    ];
    const shouldIgnoreYear =
      input.timeFilterPreset &&
      presetsIgnoringYear.includes(input.timeFilterPreset);

    // For previous year data, pass the currentYear so it uses the same reference date
    previousFilteredBreakdowns = input.timeFilterPreset
      ? filterByTimePreset(
          previousEnrichedBreakdowns,
          input.timeFilterPreset,
          new Date(),
          shouldIgnoreYear,
          input.currentYear,
        )
      : previousEnrichedBreakdowns;
  }

  const shouldIncludeHistoricalCombinations =
    (input.timeFilterPreset === 'yoy' ||
      input.timeFilterPreset === 'current') &&
    previousFilteredBreakdowns.length > 0;

  const dimensionCollectionBreakdowns = shouldIncludeHistoricalCombinations
    ? [...filteredBreakdowns, ...previousFilteredBreakdowns]
    : filteredBreakdowns;

  // Always ensure all combinations when primaryXDimension is TIME
  // This ensures all months appear as columns even if they have zero values
  const shouldEnsureAllCombinations =
    shouldIncludeHistoricalCombinations ||
    input.fillMissingSubdimensions ||
    input.dimensions.primaryX === DimensionType.TIME;

  const allDimensionValues = shouldEnsureAllCombinations
    ? collectAllDimensionValues(dimensionCollectionBreakdowns, input)
    : null;

  const extractInput: TransformInput = {
    ...input,
    fillMissingSubdimensions: false,
  };

  let cells = extractAndAggregate(filteredBreakdowns, extractInput);

  if (shouldEnsureAllCombinations && allDimensionValues) {
    cells = fillMissingCombinations(cells, allDimensionValues);
  }

  // STEP 5: Add YOY data if previous year exists
  const presetsWithYOY: TimeFilterPreset[] = [
    'yoy',
    'current',
    'previous',
    'prev_and_current',
    'current_and_next',
  ];

  if (
    previousFilteredBreakdowns.length > 0 &&
    input.timeFilterPreset &&
    presetsWithYOY.includes(input.timeFilterPreset)
  ) {
    let previousCells = extractAndAggregate(
      previousFilteredBreakdowns,
      extractInput,
    );

    if (shouldEnsureAllCombinations && allDimensionValues) {
      previousCells = fillMissingCombinations(
        previousCells,
        allDimensionValues,
      );
    }

    // Enhance with YOY
    const yoyCells = enhanceWithYOY(cells, previousCells);

    // For new presets, expand YOY objects into separate columns
    const presetsWithColumnExpansion: TimeFilterPreset[] = [
      'current',
      'previous',
      'prev_and_current',
      'current_and_next',
    ];

    if (
      input.timeFilterPreset &&
      presetsWithColumnExpansion.includes(input.timeFilterPreset) &&
      input.currentYear !== undefined &&
      input.previousYear !== undefined
    ) {
      return expandYOYToColumns(
        yoyCells,
        input.currentYear,
        input.previousYear,
        input.dimensions.primaryX,
      );
    }

    // For 'yoy' preset, return YOY objects as-is (stacked display)
    return yoyCells;
  }

  return cells;
}
